<template>
  <m-pageframe title="RedChirp SSO Settings">
    <v-container>
      <card-grid-with-controls :show_loading_state="loading">
        <template #header-actions>
          <v-btn color="primary" @click="openDialog()" :disabled="loading">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Create SSO Setting
          </v-btn>
        </template>

        <template #filter>
            <v-btn-toggle v-model="filter_status" @change="applyFilters" dense mandatory>
              <v-btn value="active">Active</v-btn>
              <v-btn value="archived">Archived</v-btn>
              <v-btn value="all">All</v-btn>
            </v-btn-toggle>
        </template>

        <template #search>
            <v-text-field
              v-model="full_text"
              prepend-inner-icon="mdi-magnify"
              label="Search"
              dense
              outlined
              clearable
              hide-details
              @input="applyFilters"
            />
        </template>

        <template #grid>
          <v-col
            v-for="item in filteredItems"
            :key="item.id"
            cols="12"
            md="6"
            lg="4"
            xl="3"
          >
            <card-with-action-header
              :header_text="item.label"
              :menu_items="getMenuItems(item)"
              :menu_aria_label="`Actions for ${item.label}`"
              :aria_label="`Edit SSO Setting: ${item.label}`"
              :card_classes="{ archived: item.archived }"
              @click="openDialog(item)"
              @menu-action="onMenuAction"
              :interactive="true"
            >
              <div class="pa-4">
                <div class="text-caption text--secondary mb-2">Provider</div>
                <div class="mb-4">{{ displayProvider(item.provider) }}</div>
                <div class="text-caption text--secondary mb-2">Auth0 Domain</div>
                <div class="mb-4">{{ item.auth0_domain || '—' }}</div>
                <div class="text-caption text--secondary mb-2">Auth0 Client ID</div>
                <div class="mb-4">{{ item.auth0_client_id || '—' }}</div>
              </div>
            </card-with-action-header>
          </v-col>
        </template>
      </card-grid-with-controls>

      <v-dialog v-model="dialog" max-width="720">
        <v-card>
          <v-card-title class="headline">{{ form.id ? 'Edit SSO Setting' : 'Create SSO Setting' }}</v-card-title>
          <v-card-text>
            <!-- Add text that this likely a devleoper thing to modify and to not touch if you don't know what you're doing -->
             <v-alert v-if="form.id" type="warning" dense text class="mt-2">
              Modifying these settings will affect existing organizations using this SSO setting.  Please ensure you understand the implications before proceeding.
            </v-alert>
            <v-divider class="mb-4" />

            <v-form>
              <v-text-field v-model="form.label" label="Label (required)" dense />
              <m-edit-select-enum
                label="SSO Provider"
                v-model="form.provider"
                enum_name="sso_provider"
                dialog_title="Select SSO Provider"
                :allow_any_enum_value="false"
              />
              <v-text-field v-model="form.auth0_domain" label="Auth0 Domain" dense />
              <v-text-field v-model="form.auth0_client_id" label="Auth0 Client ID" dense />
              <v-text-field v-model="auth0_client_secret_plaintext" :type="show_secret ? 'text' : 'password'" label="Auth0 Client Secret (only set to update)" dense append-icon="mdi-eye" @click:append="show_secret = !show_secret" />
              <!--Add text to archived that it's only visual, users will still be able to use it but no longer selectable-->
              <v-switch v-model="form.archived" label="Archived" dense class="mt-2" />
              <v-alert type="info" v-if="form.id"  dense text class="mt-2">
                Archiving a setting will make it unavailable for selection in organizations, but will not affect existing organizations using the setting currently.
              </v-alert>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </m-pageframe>
</template>

<script>
export default {
  name: 'admin-red-chirp-sso-settings',
  components: {
    CardGridWithControls: () => import('@/components/layouts/card-grid-with-controls.vue'),
    CardWithActionHeader: () => import('@/components/shared/cards/card-with-action-header.vue'),
  },
  data() {
    return {
      loading: false,
      full_text: '',
      filter_status: 'active',
      dialog: false,
      form: {
        id: null,
        label: '',
        provider: 'auth0',
        auth0_domain: '',
        auth0_client_id: '',
        archived: false,
      },
      auth0_client_secret_plaintext: '',
      show_secret: false,
      saving: false,
      items: [],
      headers: [
        { text: 'Label', value: 'label' },
        { text: 'Provider', value: 'provider' },
        { text: 'Auth0 Domain', value: 'auth0_domain' },
        { text: 'Auth0 Client ID', value: 'auth0_client_id' },
        { text: 'Status', value: 'archived' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    }
  },
  mounted() {
    this.fetch();
  },
  computed: {
    filteredItems() {
      const text = (this.full_text || '').trim().toLowerCase();
      return (this.items || [])
        .filter(x => !text || (x.label || '').toLowerCase().includes(text) || (x.auth0_domain || '').toLowerCase().includes(text))
        .filter(x => this.filter_status === 'all' ? true : (this.filter_status === 'archived' ? !!x.archived : !x.archived));
    }
  },
  methods: {
    displayProvider(provider) {
      return (provider && provider.name) ? provider.name : provider;
    },
    getMenuItems(item) {
      return [
        { text: 'Edit', icon: 'mdi-pencil', action: 'edit' },
        { text: item.archived ? 'Unarchive' : 'Archive', icon: item.archived ? 'mdi-archive-arrow-up' : 'mdi-archive', action: 'toggle-archive' },
      ];
    },
    onMenuAction(action, item) {
      if (action === 'edit') {
        this.openDialog(item);
      } else if (action === 'toggle-archive') {
        this.toggleArchived(item);
      }
    },
    applyFilters() {
      // computed handles it; this exists for clarity and future hooks
    },
    openDialog(item) {
      if (item) {
        this.form = Object.assign({}, this.form, item);
      } else {
        this.form = { id: null, label: '', provider: 'auth0', auth0_domain: '', auth0_client_id: '', archived: false };
      }
      this.auth0_client_secret_plaintext = '';
      this.dialog = true;
    },
    fetch() {
      this.loading = true;
      this.$rc.rest({
        endpoint: 'LIST: /api/v1/admin/auth/red-chirp-sso-settings',
        params: {},
        spinner: false,
        on_success: (data) => {
          this.items = (data.results || []).map(x => ({
            id: x.id,
            label: x.label,
            provider: (x.provider && x.provider.name) ? x.provider.name : x.provider,
            auth0_domain: x.auth0_domain || '',
            auth0_client_id: x.auth0_client_id || '',
            archived: !!x.archived,
          }));
          this.loading = false;
        },
        on_error: () => { this.loading = false; this.$rc.alert('Error', 'Unable to load central SSO settings'); }
      });
    },
    toggleArchived(item) {
      const payload = { id: item.id, label: item.label, provider: item.provider, auth0_domain: item.auth0_domain, auth0_client_id: item.auth0_client_id, archived: !item.archived };
      this.$rc.rest({
        endpoint: 'PUT: /api/v1/admin/auth/red-chirp-sso-settings',
        params: payload,
        spinner: true,
        on_success: () => { this.fetch(); },
        on_error: () => this.$rc.alert('Error', 'Unable to update archived status')
      });
    },
    save() {
      if (!this.form.label || !this.form.provider) {
        this.$rc.alert('Error', 'Label and Provider are required');
        return;
      }
      const payload = Object.assign({}, this.form);
      if (this.auth0_client_secret_plaintext) {
        payload.auth0_client_secret_plaintext = this.auth0_client_secret_plaintext;
      }
      this.saving = true;
      this.$rc.rest({
        endpoint: 'PUT: /api/v1/admin/auth/red-chirp-sso-settings',
        params: payload,
        spinner: true,
        on_success: () => {
          this.$rc.snackbar('Saved');
          this.saving = false;
          this.auth0_client_secret_plaintext = '';
          this.dialog = false;
          this.fetch();
        },
        on_error: () => {
          this.saving = false;
          this.$rc.alert('Error', 'Unable to save');
        }
      });
    }
  }
}
</script>

<style scoped>
</style>

